<script setup>
import {ref, watch, computed} from 'vue'
import {useI18n} from 'vue-i18n'
import $http from '@/axios.config'
import helpers from '../../.././helpers.js'
import store from '@/store'
import {log} from "@/utils/clientLogger.js";

const props = defineProps({
  modelValue: String
})

//const emit = defineEmits(['toOrder'])

const {t} = useI18n({useScope: 'global'})

const userRole = computed(() => store.getters.userData?.role?.name)
const isDistributor = computed(() => userRole.value === 'distributor')

const activeClientId = ref(props.modelValue || null)
const clientOrders = ref([])

const getOrders = () => {
  if (activeClientId.value) {
    $http.get(`/api/clients/${activeClientId.value}/orders`).then(response => {
      clientOrders.value = response.data
    }).catch(error => {
      log.error('View:AllOrdersTable:getOrders', {'error': error});
    })
  }
}

const getOrderTitle = (order) => {
  if (order.carBrand) {
    let title = order.carBrand;
    if (order.carModel) title += ' ' + order.carModel;
    if (order.carYear) title += ' ' + order.carYear;
    return title;
  }
  return order.title;
};

watch(() => props.modelValue, (newVal) => {
  activeClientId.value = newVal
  getOrders()
}, {immediate: true})

const convertDate = (date) => helpers.convertTime(date)
const assemblyProgramLabels = (values) => helpers.getAssemblyProgramLabels(values)

const isOrderFromDistributor = (order) => {
  return order?.managerId?.role?.name === 'distributor'
}
</script>

<template>
  <div class="table-responsive">
    <table class="table table-hover">
      <thead>
      <tr>
        <th scope="col">id</th>
        <th scope="col">{{ t('tableheadings.name') }}</th>
        <th scope="col" v-if="!isDistributor">{{ t('tableheadings.manager') }}</th>
        <th scope="col">{{ t('tableheadings.draftsman') }}</th>
        <th scope="col">{{ t('tableheadings.created') }}</th>
        <th scope="col">{{ t('tableheadings.status') }}</th>
      </tr>
      </thead>
      <tbody>
      <tr class="pointer" v-for="order in clientOrders" :key="order._id">
        <th scope="row">{{ order.humanId }}</th>
        <td>
          <router-link class="navbar-brand" target="_blank" :to="'/order/' + order._id">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-external-link"
                 width="22" height="22" viewBox="0 0 24 24" stroke-width="1.5" stroke="#9e9e9e"
                 fill="none" stroke-linecap="round" stroke-linejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6"/>
              <path d="M11 13l9 -9"/>
              <path d="M15 4h5v5"/>
            </svg>
          </router-link>
          {{ getOrderTitle(order) }}
          <div class="mt-1 d-flex flex-wrap gap-1">
            <span v-if="order.hasProblematicPattern" class="badge d-inline-flex align-items-center gap-1 bg-warning text-dark">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 9v4" />
                <path d="M12 16v.01" />
                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
              </svg>
              {{ t('labels.problemPattern', 'Проблемна викройка') }}
            </span>
            <span v-if="order.hasProblematicPackaging" class="badge d-inline-flex align-items-center gap-1 bg-warning text-dark">
               <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 9v4" />
                <path d="M12 16v.01" />
                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
              </svg>
              {{ t('labels.problemPackaging', 'Проблема упаковки') }}
            </span>
            <span v-if="order.wasCut && (order.status === 'done' || order.status === 'closed')" class="badge d-inline-flex align-items-center gap-1 bg-info text-dark">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M9.15 14.85l8.85 -10.85" />
                <path d="M7 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                <path d="M17 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                <path d="M9.15 9.15l8.85 10.85" />
              </svg>
              {{ t('log.fields.wasCutBadge', 'Різалось') }}
            </span>
            <span v-if="order.assemblyPrograms && order.assemblyPrograms.length" class="badge bg-primary text-white mw-100 text-wrap text-break text-start  d-inline-flex align-items-center"
                  :title="t('labels.assemblyProgram', 'Програма збірки лекала')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1" style="vertical-align:-0.15em;">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M4 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                <path d="M16 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                <path d="M4 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                <path d="M16 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
              </svg>{{ t('labels.assembledIn', 'Зібрано') }}: {{ assemblyProgramLabels(order.assemblyPrograms) }}
            </span>
            <span v-if="order.hasNoPatterns && order.noPatternsType === 'partial'" class="badge d-inline-flex align-items-center gap-1 bg-warning text-dark">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M4 7h3a1 1 0 0 0 1 -1v-1a2 2 0 0 1 4 0v1a1 1 0 0 0 1 1h3a1 1 0 0 1 1 1v3a1 1 0 0 0 1 1h1a2 2 0 0 1 0 4h-1a1 1 0 0 0 -1 1v3a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1v-1a2 2 0 0 0 -4 0v1a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1h1a2 2 0 0 0 0 -4h-1a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1" />
              </svg>
              {{ t('labels.noPatternsPartialBadge', 'Є частково') }}
            </span>
            <span v-else-if="order.hasNoPatterns" class="badge d-inline-flex align-items-center gap-1 bg-secondary text-white"
                  :style="order.patternRequestSent ? 'background-color:#f15a24 !important;' : ''">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M3 3l18 18" />
                <path d="M4 7v-2a1 1 0 0 1 1 -1h2" />
                <path d="M4 17v2a1 1 0 0 0 1 1h2" />
                <path d="M16 4h3a1 1 0 0 1 1 1v3" />
                <path d="M20 16v3a1 1 0 0 1 -1 1h-3" />
              </svg>
              {{ order.noPatternsType === 'none_in_ours' ? t('labels.noPatternsNoneInOurs', 'Немає лекал в наших базах') : t('labels.noPatternsNoneInAny', 'Немає лекал в жодній з баз') }}<template v-if="order.patternRequestSent"> · {{ t('labels.patternRequestSent', 'Відправлено запит на отримання лекал') }}</template>
            </span>
          </div>
        </td>
        <td v-if="!isDistributor">
            <span v-if="isOrderFromDistributor(order)" class="badge bg-danger me-1">
              {{ t('tableheadings.distributor') }}
            </span>
          {{ order?.managerId?.username }}
        </td>
        <td>{{ order?.draftsmanId?.username }}</td>
        <td>{{ convertDate(order?.createdAt) }}</td>
        <td>
          {{ order.status === 'done' ? t('tableheadings.completed') : t('tableheadings.closed') }}
          - {{ convertDate(order?.updatedAt) }}
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
.pointer {
  cursor: pointer;
}
</style>